import { useEffect } from 'react';
import { X } from 'lucide-react';

interface SheetProps {
    open: boolean;
    onClose: () => void;
    /** Accessible name for the dialog. */
    label: string;
    children: React.ReactNode;
    className?: string;
}

/**
 * Bottom sheet on mobile, centred dialog from `sm` up.
 *
 * Shared by the plan-details view and the header callback CTA so escape
 * handling, scroll locking and click-outside behave identically in both.
 */
export default function Sheet({ open, onClose, label, children, className = '' }: SheetProps) {
    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;

        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-[#15171C]/40 backdrop-blur-[2px] sm:items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={onClose}
        >
            <div
                className={`relative max-h-[88dvh] w-full overflow-y-auto rounded-t-3xl border border-[#E4E1DA] bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-2xl sm:p-6 ${className}`}
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 rounded-lg p-1.5 text-[#9A958B] transition-colors hover:bg-[#F2F0EC] hover:text-[#15171C]"
                >
                    <X className="h-4 w-4" />
                </button>
                {children}
            </div>
        </div>
    );
}
